<script setup lang="ts">
import { ref, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { useChatbotStore } from '@/stores/chatbot'
import { useMenuNav } from '@/composables/useMenuNav'
import { apiClient } from '@/api/client'
import type { TicketCategoriaDTO } from '@/modules/ticketing/types'

const { t } = useI18n()
const chatStore = useChatbotStore()
const router = useRouter()
const { findNodeBySpringRoute, openLegacyPage } = useMenuNav()
const toast = useToast()
const inputText = ref('')
const messagesEnd = ref<HTMLElement | null>(null)

// ── Feedback 👍 👎 ────────────────────────────────────────────────────────────
const feedback = ref<Record<number, 'up' | 'down'>>({})

async function giveFeedback(idx: number, val: 'up' | 'down') {
  if (feedback.value[idx]) return  // già votato
  feedback.value[idx] = val
  // Trova la domanda precedente e la risposta corrente
  const risposta = chatStore.messages[idx]?.content ?? ''
  const domanda = chatStore.messages.slice(0, idx).filter(m => m.role === 'user').at(-1)?.content ?? ''
  try {
    await apiClient.post('/api/v2/chatbot/feedback', {
      domanda,
      risposta,
      voto: val === 'up' ? 1 : -1,
    })
  } catch { /* fire-and-forget: non blocca l'UX */ }
  toast.add({ severity: 'success', summary: val === 'up' ? t('chatbot.grazie', 'Grazie!') : t('chatbot.feedbackRegistrato', 'Feedback registrato'), life: 2000 })
}

// ── Ticket assistenza ─────────────────────────────────────────────────────────
const ticketDialog = ref(false)
const ticketIdx = ref(-1)
const ticketSending = ref(false)
const ticketNote = ref('')

function openTicket(idx: number) {
  ticketIdx.value = idx
  ticketNote.value = ''
  ticketDialog.value = true
}

async function submitTicket() {
  if (ticketIdx.value < 0) return
  ticketSending.value = true
  const msg = chatStore.messages[ticketIdx.value]
  const prevUser = chatStore.messages.slice(0, ticketIdx.value).filter(m => m.role === 'user').at(-1)
  const titolo = `Assistenza chatbot: ${prevUser?.content?.slice(0, 60) ?? 'domanda'}`
  const descrizione = `**Domanda posta al chatbot:**\n${prevUser?.content ?? ''}\n\n**Risposta ricevuta:**\n${msg?.content ?? ''}\n\n**Note aggiuntive:**\n${ticketNote.value}`
  try {
    // Il backend (TicketCreateDTO) richiede: titolo, descrizione, categoriaId (Integer), priorita.
    // Scelgo una categoria attiva di default ("Altro"/"Generale" se c'è, altrimenti la prima).
    const { data: categorie } = await apiClient.get<TicketCategoriaDTO[]>('/api/v2/ticket/categorie')
    const attive = (categorie ?? []).filter(c => c.attiva)
    const categoria = attive.find(c => /altro|general/i.test(c.nome)) ?? attive[0]
    if (!categoria) {
      toast.add({ severity: 'error', summary: t('chatbot.ticketErrore', 'Errore apertura ticket'), detail: t('chatbot.nessunaCategoria', 'Nessuna categoria disponibile.'), life: 3000 })
      return
    }
    const form = new FormData()
    form.append('dati', new Blob(
      [JSON.stringify({ titolo, descrizione, categoriaId: categoria.id, priorita: 'MEDIA' })],
      { type: 'application/json' }))
    const { data: creato } = await apiClient.post('/api/v2/ticket', form, { headers: { 'Content-Type': 'multipart/form-data' } })
    toast.add({ severity: 'success', summary: t('chatbot.ticketAperto', 'Ticket aperto'), detail: t('chatbot.operatoreRispondera', 'Un operatore ti risponderà presto.'), life: 4000 })
    ticketDialog.value = false
    chatStore.isOpen = false
    // Reindirizza al ticket appena creato (se torna l'id) o alla lista
    router.push(creato?.id ? `/ticket/${creato.id}` : '/ticket')
  } catch {
    toast.add({ severity: 'error', summary: t('chatbot.ticketErrore', 'Errore apertura ticket'), life: 3000 })
  } finally {
    ticketSending.value = false
  }
}

// ── Navigazione contestuale ───────────────────────────────────────────────────
interface NavHint { label: string; icon: string; to: string }

function getNavHints(text: string): NavHint[] {
  const low = text.toLowerCase()
  const hints: NavHint[] = []
  if (/calendar|appuntament/.test(low))    hints.push({ label: t('chatbot.navCalendario', 'Calendario'), icon: 'pi pi-calendar', to: '/calendario/calendario' })
  if (/protocol/.test(low))               hints.push({ label: t('chatbot.navProtocolli', 'Protocolli'), icon: 'pi pi-book', to: '/protocollo/elenco' })
  if (/mail|posta|pec|webmail/.test(low)) hints.push({ label: t('chatbot.navWebmail', 'Webmail'), icon: 'pi pi-envelope', to: '/mail/inbox' })
  if (/ospite|beneficiar/.test(low))      hints.push({ label: t('chatbot.navOspiti', 'Ospiti'), icon: 'pi pi-users', to: '/ospiti' })
  if (/struttur|centr/.test(low))         hints.push({ label: t('chatbot.navCentri', 'Centri'), icon: 'pi pi-home', to: '/centri' })
  if (/ticket|assistenz/.test(low))       hints.push({ label: t('chatbot.navTicket', 'Ticket'), icon: 'pi pi-ticket', to: '/ticket' })
  return hints
}

/**
 * Naviga all'hint rispettando la logica del menu (is_legacy): il `to` dell'hint coincide con la
 * spring_route della funzione. Se la voce è legacy → apre il portale classico (home.seam?pagina=...);
 * altrimenti → route Vue interna. Data-driven come il menu/KPI dashboard (vedi useMenuNav).
 */
function goHint(to: string): void {
  const node = findNodeBySpringRoute(to)
  if (node?.isLegacy && node.url) {
    openLegacyPage(node.url)
  } else {
    void router.push(to)
  }
  chatStore.isOpen = false
}

// ── Markdown ─────────────────────────────────────────────────────────────────
function renderMarkdown(text: string): string {
  // Sanitizza l'HTML prodotto da marked prima del v-html (difesa XSS:
  // la risposta del modello potrebbe contenere markup non sicuro).
  const raw = marked.parse(text, { async: false }) as string
  return DOMPurify.sanitize(raw)
}

// ── Messaggi ─────────────────────────────────────────────────────────────────
async function sendMessage() {
  const text = inputText.value.trim()
  if (!text) return
  inputText.value = ''
  await chatStore.send(text)
  await nextTick()
  messagesEnd.value?.scrollIntoView({ behavior: 'smooth' })
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    sendMessage()
  }
}

watch(() => chatStore.messages.length, async () => {
  await nextTick()
  messagesEnd.value?.scrollIntoView({ behavior: 'smooth' })
})
</script>

<template>
  <Drawer
    v-model:visible="chatStore.isOpen"
    position="right"
    :style="{ width: '460px' }"
    :header="t('chatbot.titolo', 'Assistente AI Immigreer')"
  >
    <div class="flex flex-col h-full gap-3">
      <!-- Area messaggi -->
      <div
        class="flex-1 flex flex-col gap-3 overflow-y-auto pr-1"
        style="min-height: 0"
      >
        <!-- Messaggio benvenuto -->
        <div
          v-if="chatStore.messages.length === 0"
          class="flex flex-col items-center justify-center gap-2 py-8 text-center"
        >
          <i class="pi pi-comments text-4xl text-primary" />
          <p class="text-muted-color m-0 text-sm">
            {{ t('chatbot.benvenuto1', 'Ciao! Sono l\'assistente AI di Immigreer.') }}<br>
            {{ t('chatbot.benvenuto2', 'Chiedimi informazioni sul sistema o sui tuoi dati.') }}<br>
            {{ t('chatbot.esempio', 'Es:') }} <em>{{ t('chatbot.esempioDomanda', '"Quanti ospiti ho oggi?"') }}</em>
          </p>
        </div>

        <!-- Messaggi -->
        <template
          v-for="(msg, idx) in chatStore.messages"
          :key="idx"
        >
          <!-- Messaggio utente -->
          <div
            v-if="msg.role === 'user'"
            class="flex justify-end"
          >
            <div
              class="max-w-xs rounded-2xl rounded-tr-sm px-4 py-2 text-sm"
              style="background: var(--p-primary-color); color: var(--p-primary-contrast-color)"
            >
              {{ msg.content }}
            </div>
          </div>
          <!-- Messaggio assistant — markdown + azioni contestuali -->
          <div
            v-else
            class="flex flex-col gap-1 items-start max-w-sm"
          >
            <div
              class="chatbot-assistant-msg w-full rounded-2xl rounded-tl-sm px-4 py-2 text-sm"
              style="background: var(--p-surface-100); color: var(--p-text-color)"
              v-html="renderMarkdown(msg.content)"
            />

            <!-- Barra azioni sotto la risposta -->
            <div class="flex flex-wrap items-center gap-1 pl-1">
              <!-- Feedback 👍 👎 -->
              <button
                class="chatbot-action-btn"
                :class="{ 'chatbot-action-active-up': feedback[idx] === 'up' }"
                :title="feedback[idx] === 'up' ? t('chatbot.rispostaUtile', 'Risposta utile') : t('chatbot.utile', 'Utile')"
                @click="giveFeedback(idx, 'up')"
              >
                <i
                  class="pi pi-thumbs-up"
                  style="font-size: 0.7rem"
                />
              </button>
              <button
                class="chatbot-action-btn"
                :class="{ 'chatbot-action-active-down': feedback[idx] === 'down' }"
                :title="feedback[idx] === 'down' ? t('chatbot.rispostaNonUtile', 'Risposta non utile') : t('chatbot.nonUtile', 'Non utile')"
                @click="giveFeedback(idx, 'down')"
              >
                <i
                  class="pi pi-thumbs-down"
                  style="font-size: 0.7rem"
                />
              </button>

              <span class="chatbot-action-sep" />

              <!-- Richiedi assistenza -->
              <button
                class="chatbot-action-btn chatbot-action-ticket"
                :title="t('chatbot.apriUnTicket', 'Non hai trovato la risposta? Apri un ticket')"
                @click="openTicket(idx)"
              >
                <i
                  class="pi pi-ticket"
                  style="font-size: 0.7rem"
                />
                <span>{{ t('chatbot.richiediAssistenza', 'Richiedi assistenza') }}</span>
              </button>

              <!-- Link contestuali -->
              <template
                v-for="hint in getNavHints(msg.content)"
                :key="hint.to"
              >
                <button
                  class="chatbot-action-btn chatbot-action-nav"
                  :title="t('chatbot.vaiA', { dest: hint.label }, 'Vai a {dest}')"
                  @click="goHint(hint.to)"
                >
                  <i
                    :class="hint.icon"
                    style="font-size: 0.7rem"
                  />
                  <span>{{ hint.label }}</span>
                </button>
              </template>
            </div>
          </div>
        </template>

        <!-- Loading bubble -->
        <div
          v-if="chatStore.isLoading"
          class="flex justify-start"
        >
          <div
            class="rounded-2xl rounded-tl-sm px-4 py-2"
            style="background: var(--p-surface-100)"
          >
            <ProgressSpinner
              style="width: 20px; height: 20px"
              stroke-width="4"
            />
          </div>
        </div>

        <div ref="messagesEnd" />
      </div>

      <!-- Input -->
      <div class="flex gap-2 items-center pt-2 border-t border-surface">
        <InputText
          v-model="inputText"
          :placeholder="t('chatbot.scriviMessaggio', 'Scrivi un messaggio...')"
          class="flex-1"
          :disabled="chatStore.isLoading"
          @keydown="onKeydown"
        />
        <Button
          icon="pi pi-send"
          rounded
          :disabled="!inputText.trim() || chatStore.isLoading"
          @click="sendMessage"
        />
      </div>
    </div>
  </Drawer>

  <!-- Dialog ticket assistenza -->
  <Dialog
    v-model:visible="ticketDialog"
    modal
    :header="t('chatbot.richiediAssistenza', 'Richiedi assistenza')"
    :style="{ width: '400px' }"
  >
    <div class="flex flex-col gap-3 pt-1 text-sm">
      <p class="m-0 text-muted-color">
        {{ t('chatbot.dialogInfo', 'Verrà aperto un ticket con il contesto della conversazione. Aggiungi eventuali dettagli aggiuntivi:') }}
      </p>
      <Textarea
        v-model="ticketNote"
        rows="4"
        :placeholder="t('chatbot.descriviMeglio', 'Descrivi meglio il problema o aggiungi informazioni utili...')"
        class="w-full"
        auto-resize
      />
    </div>
    <template #footer>
      <Button
        :label="t('common.annulla', 'Annulla')"
        text
        severity="secondary"
        @click="ticketDialog = false"
      />
      <Button
        :label="t('chatbot.apriTicket', 'Apri ticket')"
        icon="pi pi-ticket"
        :loading="ticketSending"
        @click="submitTicket"
      />
    </template>
  </Dialog>
</template>

<style scoped>
/* ── Barra azioni sotto la risposta ── */
.chatbot-action-btn {
  display: inline-flex; align-items: center; gap: 0.25rem;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  border: 1px solid var(--p-surface-300);
  background: var(--p-surface-0);
  color: var(--p-text-muted-color);
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.chatbot-action-btn:hover {
  background: var(--p-surface-100);
  color: var(--p-text-color);
  border-color: var(--p-surface-400);
}
.chatbot-action-active-up  { color: #22c55e !important; border-color: #22c55e !important; }
.chatbot-action-active-down { color: #ef4444 !important; border-color: #ef4444 !important; }
.chatbot-action-ticket { color: var(--p-primary-color); border-color: var(--p-primary-200); }
.chatbot-action-ticket:hover { background: var(--p-primary-50); }
.chatbot-action-nav { color: var(--p-indigo-500, #6366f1); border-color: var(--p-indigo-200, #c7d2fe); }
.chatbot-action-nav:hover { background: var(--p-indigo-50, #eef2ff); }
.chatbot-action-sep { display: inline-block; width: 1px; height: 14px; background: var(--p-surface-300); margin: 0 2px; }

.chatbot-assistant-msg :deep(p) { margin: 0 0 0.5rem 0; }
.chatbot-assistant-msg :deep(p:last-child) { margin-bottom: 0; }
.chatbot-assistant-msg :deep(strong) { font-weight: 600; }
.chatbot-assistant-msg :deep(ul),
.chatbot-assistant-msg :deep(ol) { margin: 0.25rem 0 0.5rem 0; padding-left: 1.25rem; }
.chatbot-assistant-msg :deep(li) { margin-bottom: 0.2rem; line-height: 1.5; }
.chatbot-assistant-msg :deep(ol) { list-style-type: decimal; }
.chatbot-assistant-msg :deep(ul) { list-style-type: disc; }
.chatbot-assistant-msg :deep(code) {
  background: var(--p-surface-200); border-radius: 3px;
  padding: 0.1em 0.3em; font-size: 0.85em; font-family: monospace;
}
.chatbot-assistant-msg :deep(h1),
.chatbot-assistant-msg :deep(h2),
.chatbot-assistant-msg :deep(h3) {
  font-weight: 600; margin: 0.5rem 0 0.25rem 0; font-size: 0.95em;
}
</style>
